import type { TelstraMobileLineItem } from '../services/telstraService';

export const isYes = (v: string | undefined | null): boolean =>
  !!v && ['YES', 'Y', 'TRUE', '1'].includes(String(v).trim().toUpperCase());

export type InactiveSummary = {
  inactive6m: TelstraMobileLineItem[];
  inactive3mOnly: TelstraMobileLineItem[];
  annualSavings6m: number;
  annualSavings3m: number;
};

export function isStale6m(r: TelstraMobileLineItem): boolean {
  return (
    isYes(r.noNetworkActivity6m) ||
    isYes(r.noUsage6m) ||
    isYes(r.onlyDiversionUsage6m) ||
    isYes(r.onlyMessagebankDiversionUsage6m)
  );
}

export function isStale3m(r: TelstraMobileLineItem): boolean {
  return (
    isYes(r.noNetworkActivity3m) ||
    isYes(r.noUsage3m) ||
    isYes(r.onlyDiversionUsage3m) ||
    isYes(r.onlyMessagebankDiversionUsage3m)
  );
}

export function summariseInactive(rows: TelstraMobileLineItem[]): InactiveSummary {
  const inactive6m = rows.filter(isStale6m);
  const inactive3mOnly = rows.filter(
    r => isStale3m(r) && !isStale6m(r),
  );
  const annualSavings6m = inactive6m.reduce((s, r) => s + (Number(r.monthlyFee) || 0) * 12, 0);
  const annualSavings3m = inactive3mOnly.reduce((s, r) => s + (Number(r.monthlyFee) || 0) * 12, 0);
  return { inactive6m, inactive3mOnly, annualSavings6m, annualSavings3m };
}

export type InactivityTier = 'none' | '3m' | '6m';

export function inactivityTier(r: TelstraMobileLineItem): InactivityTier {
  if (isStale6m(r)) return '6m';
  if (isStale3m(r)) return '3m';
  return 'none';
}

function burstRatio(series: number[] | undefined, opts: { minAbsolute: number }): number {
  if (!series || series.length < 4) return 0;
  const latest = Number(series[0]) || 0;
  const tail = series.slice(1).map(n => Number(n) || 0);
  const mean = tail.reduce((s, n) => s + n, 0) / tail.length;
  if (mean < opts.minAbsolute) return 0;
  if (latest <= mean) return 0;
  return latest / mean;
}

export type BurstFlags = {
  data: boolean;
  cost: boolean;
  dataRatio: number;
  costRatio: number;
  dataDeltaMb: number;
  costDelta: number;
};

const BURST_THRESHOLD = 1.5;

export function detectUsageBurst(r: TelstraMobileLineItem): BurstFlags {
  const dataRatio = burstRatio(r.monthlyDataMb, { minAbsolute: 500 });
  const costRatio = burstRatio(r.monthlyBillTotal, { minAbsolute: 10 });
  const meanData = (r.monthlyDataMb && r.monthlyDataMb.length > 1)
    ? r.monthlyDataMb.slice(1).reduce((s, n) => s + (Number(n) || 0), 0) / (r.monthlyDataMb.length - 1)
    : 0;
  const meanCost = (r.monthlyBillTotal && r.monthlyBillTotal.length > 1)
    ? r.monthlyBillTotal.slice(1).reduce((s, n) => s + (Number(n) || 0), 0) / (r.monthlyBillTotal.length - 1)
    : 0;
  const latestData = Number(r.monthlyDataMb?.[0]) || 0;
  const latestCost = Number(r.monthlyBillTotal?.[0]) || 0;
  return {
    data: dataRatio >= BURST_THRESHOLD,
    cost: costRatio >= BURST_THRESHOLD,
    dataRatio,
    costRatio,
    dataDeltaMb: Math.max(0, latestData - meanData),
    costDelta: Math.max(0, latestCost - meanCost),
  };
}

export function hasBurst(r: TelstraMobileLineItem): boolean {
  const b = detectUsageBurst(r);
  return b.data || b.cost;
}
